import { HttpClient } from '@angular/common/http';
import { computed, Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AppLanguage, DEFAULT_LANGUAGE, LANGUAGE_BY_CODE, isAppLanguage } from '../i18n/languages';

export type LanguageChangeSource = 'initial' | 'manual' | 'backend';

type TranslationTree = Record<string, unknown>;

export interface LanguageSwitchOverlay {
  language: AppLanguage;
  source: Exclude<LanguageChangeSource, 'initial'>;
  flag: string;
  label: string;
  message: string;
}

const STORAGE_KEY = 'cr_language';
const LANGUAGE_SWITCH_OVERLAY_MS = 3000;

@Injectable({ providedIn: 'root' })
export class I18nService {
  private readonly http = inject(HttpClient);
  private readonly translations = signal<TranslationTree>({});
  private readonly companyName = signal('Creditreform');
  private readonly loaded = new Map<AppLanguage, TranslationTree>();
  private readonly changeSource = signal<LanguageChangeSource>('initial');
  private readonly overlay = signal<LanguageSwitchOverlay | null>(null);
  private overlayTimer: ReturnType<typeof setTimeout> | null = null;
  private languageLoadSequence = 0;

  readonly language = signal<AppLanguage>(this.normalizeLanguage(localStorage.getItem(STORAGE_KEY) ?? DEFAULT_LANGUAGE));
  readonly locale = computed(() => LANGUAGE_BY_CODE[this.language()].locale);
  readonly direction = computed(() => LANGUAGE_BY_CODE[this.language()].direction);
  readonly languageChangeSource = this.changeSource.asReadonly();
  readonly languageSwitchOverlay = this.overlay.asReadonly();

  async loadInitial(): Promise<void> {
    await this.setLanguage(this.language(), 'initial');
  }

  async setLanguage(language: string, source: LanguageChangeSource = 'manual'): Promise<void> {
    const previous = this.language();
    const next = this.normalizeLanguage(language);
    const hasChanged = next !== previous;
    const sequence = ++this.languageLoadSequence;

    let translations: TranslationTree;
    try {
      translations = await this.loadTranslations(next);
    } catch (error) {
      if (sequence === this.languageLoadSequence) {
        this.applyLanguageMetadata(previous);
      }

      throw error;
    }

    if (sequence !== this.languageLoadSequence) {
      return;
    }

    this.changeSource.set(source);
    this.translations.set(translations);
    this.language.set(next);
    this.applyLanguageMetadata(next);

    if (hasChanged && source !== 'initial') {
      this.showLanguageSwitchOverlay(next, source, translations);
    }
  }

  setCompanyName(companyName: string): void {
    this.companyName.set(companyName);
  }

  t(key: string, params: Record<string, string | number> = {}): string {
    return this.translateFrom(this.translations(), key, { company: this.companyName(), ...params });
  }

  has(key: string): boolean {
    const value = this.resolve(this.translations(), key);
    return typeof value === 'string' && !value.startsWith('rosetta:');
  }

  formatCurrency(value: number): string {
    return value.toLocaleString(this.locale(), {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
    return value.toLocaleString(this.locale(), options);
  }

  formatDate(value: Date | string): string {
    if (typeof value === 'string') {
      const isoDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
      if (isoDate) {
        const [, year, month, day] = isoDate;
        const parsed = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
        if (parsed.getUTCFullYear() === Number(year)
          && parsed.getUTCMonth() === Number(month) - 1
          && parsed.getUTCDate() === Number(day)) {
          return `${day}.${month}.${year}`;
        }

        return value;
      }
    }

    const parsed = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(parsed.getTime())) {
      return typeof value === 'string' ? value : '';
    }

    return parsed.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  private normalizeLanguage(value: unknown): AppLanguage {
    if (!isAppLanguage(value)) {
      throw new Error(`Unsupported language: ${String(value)}`);
    }

    return value;
  }

  private applyLanguageMetadata(language: AppLanguage): void {
    localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = LANGUAGE_BY_CODE[language].locale;
    document.documentElement.dir = LANGUAGE_BY_CODE[language].direction;
    document.body.classList.toggle('is-rtl', LANGUAGE_BY_CODE[language].direction === 'rtl');
  }

  private async loadTranslations(language: AppLanguage): Promise<TranslationTree> {
    const cached = this.loaded.get(language);
    if (cached) {
      return cached;
    }

    const translations = await firstValueFrom(this.http.get<TranslationTree>(`/i18n/${language}.json`));
    this.loaded.set(language, translations);
    return translations;
  }

  private showLanguageSwitchOverlay(
    language: AppLanguage,
    source: Exclude<LanguageChangeSource, 'initial'>,
    translations: TranslationTree
  ): void {
    if (this.overlayTimer) {
      clearTimeout(this.overlayTimer);
    }

    const label = this.translateFrom(translations, 'language.switchOverlay.name');
    this.overlay.set({
      language,
      source,
      flag: this.translateFrom(translations, 'language.switchOverlay.flag'),
      label,
      message: this.translateFrom(translations, 'language.switchOverlay.message', { language: label }),
    });

    this.overlayTimer = setTimeout(() => {
      this.overlay.set(null);
      this.overlayTimer = null;
    }, LANGUAGE_SWITCH_OVERLAY_MS);
  }

  private translateFrom(tree: TranslationTree, key: string, params: Record<string, string | number> = {}): string {
    const value = this.resolve(tree, key);
    if (typeof value !== 'string' || value.startsWith('rosetta:')) {
      throw new Error(`Missing translation: ${key}`);
    }

    return value.replace(/\{\{([^{}]+)\}\}/g, (_marker, name: string) => {
      const replacement = Object.hasOwn(params, name) ? params[name] : undefined;
      if (typeof replacement !== 'string' && (typeof replacement !== 'number' || !Number.isFinite(replacement))) {
        throw new Error(`Missing translation parameter: ${key}.${name}`);
      }

      return String(replacement);
    });
  }

  private resolve(tree: TranslationTree, key: string): unknown {
    return key.split('.').reduce<unknown>((current, part) => {
      if (!current || typeof current !== 'object') {
        return undefined;
      }

      return (current as Record<string, unknown>)[part];
    }, tree);
  }
}
